class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 140).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    // The plain term matched no products, so the results page would be empty.
    // Send it the wildcard form that the dropdown is already showing.
    if (this.fallbackTerm && this.fallbackTerm === this.getQuery()) {
      event.preventDefault();
      const base = (typeof routes !== 'undefined' && routes.search_url) || '/search';
      window.location.assign(base + '?q=' + encodeURIComponent(this.fallbackTerm + '*') + '&type=product');
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    // Group-heading rows carry role="presentation" and must not be arrow-selectable.
    const allElements = Array.from(this.querySelectorAll('li[role="option"]'));
    if (!allElements.length) return;
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (selectedElement) {
      const currentIndex = allElements.indexOf(selectedElement);
      const nextIndex = moveUp ? currentIndex - 1 : currentIndex + 1;
      activeElement = allElements[(nextIndex + allElements.length) % allElements.length];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.fallbackTerms = this.fallbackTerms || {};
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.fallbackTerm = this.fallbackTerms[queryKey] ? searchTerm : null;
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const endpoint = (term) =>
      `${routes.predictive_search_url}?q=${encodeURIComponent(term)}&${encodeURIComponent('resources[type]')}=product,collection&${encodeURIComponent('resources[limit]')}=6&${encodeURIComponent('resources[options][unavailable_products]')}=last&section_id=predictive-search`;

    const grab = (term) => fetch(endpoint(term)).then((response) => (response.ok ? response.text() : ''));

    // Shopify's index prefix-matches only a subset of terms: "plimmerton" finds four
    // products, "plim" finds none. A trailing wildcard matches partial words but
    // reranks everything, so ask for both and keep the plain ranking on top.
    const wildTerm = /[*"]/.test(searchTerm) || searchTerm.length < 2 ? null : searchTerm + '*';

    Promise.all([grab(searchTerm), wildTerm ? grab(wildTerm) : Promise.resolve('')])
      .then(([plainText, wildText]) => {
        if (!plainText) {
          this.close();
          return;
        }

        const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
        const isProduct = (li) => /^predictive-search-option-\d+$/.test(li.id);
        const hrefOf = (li) => {
          const a = li.querySelector('a[href]');
          return a ? a.getAttribute('href').split('?')[0] : '';
        };

        const plainDoc = parse(plainText);
        const plainList = plainDoc.querySelector('#predictive-search-results-list');
        const plainProducts = plainList ? Array.from(plainList.children).filter(isProduct) : [];

        let doc = plainDoc;
        let usedWildcard = false;

        if (wildText) {
          const wildDoc = parse(wildText);
          const wildList = wildDoc.querySelector('#predictive-search-results-list');
          const wildProducts = wildList ? Array.from(wildList.children).filter(isProduct) : [];

          if (plainProducts.length === 0 && wildProducts.length > 0) {
            doc = wildDoc;
            usedWildcard = true;
          } else if (plainList && wildProducts.length > 0) {
            const seen = new Set(plainProducts.map(hrefOf));
            const keywordsItem = plainList.querySelector('#predictive-search-option-search-keywords');
            let count = plainProducts.length;

            wildProducts.forEach((li) => {
              const href = hrefOf(li);
              if (count >= 8 || !href || seen.has(href)) return;
              seen.add(href);
              count += 1;
              li.id = 'predictive-search-option-' + count;
              if (keywordsItem) plainList.insertBefore(li, keywordsItem);
              else plainList.appendChild(li);
            });
          }
        }

        if (usedWildcard) {
          // Show the user their term, not the wildcard we searched with.
          const strip = (el) => {
            if (el) el.innerHTML = el.innerHTML.split(searchTerm + '*').join(searchTerm);
          };
          strip(doc.querySelector('#predictive-search-option-search-keywords button'));
          strip(doc.querySelector('[data-predictive-search-live-region-count-value]'));
        }

        this.fallbackTerm = usedWildcard ? searchTerm : null;
        this.fallbackTerms[queryKey] = usedWildcard;

        const section = doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
